import { createContext, useContext, useEffect, useState } from 'react';
import { apiFetch } from '../lib/apiFetch';
import { useAuth } from './AuthContext';

type HalLink = { href: string };
type RootLinks = Record<string, HalLink>;

interface ApiRootContextType {
  links: RootLinks | null;
  loading: boolean;
  hasLink: (rel: string) => boolean;
  getHref: (rel: string) => string | null;
}

const ApiRootContext = createContext<ApiRootContextType | null>(null);

export function ApiRootProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [links, setLinks] = useState<RootLinks | null>(null);

  useEffect(() => {
    if (!user) {
      setLinks(null);
      return;
    }

    let cancelled = false;
    setLinks(null);

    (async () => {
      try {
        const response = await apiFetch('/api/v1/root');
        if (!response.ok) {
          throw new Error(`Root request failed: ${response.status}`);
        }
        const data = (await response.json()) as { _links?: RootLinks };
        if (!cancelled) {
          setLinks(data._links ?? {});
        }
      } catch (err) {
        console.error('Failed to fetch API root:', err);
        if (!cancelled) {
          setLinks({});
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user]);

  const loading = Boolean(user) && links === null;
  const hasLink = (rel: string) => Boolean(links?.[rel]);
  const getHref = (rel: string) => links?.[rel]?.href ?? null;

  return (
    <ApiRootContext.Provider value={{ links, loading, hasLink, getHref }}>
      {children}
    </ApiRootContext.Provider>
  );
}

export function useApiRoot() {
  const ctx = useContext(ApiRootContext);
  if (!ctx) throw new Error('useApiRoot must be used inside ApiRootProvider');
  return ctx;
}
